import { useEffect, useState } from 'react';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';
import { currencyFormatter } from 'utils/functions';

import { HAND_END_BUTTON_MAX_WIDTH } from 'components/UserActions/PokerActions/ActionButton';
import CheckboxAction from 'components/UserActions/PokerActions/CheckboxAction';
import { useServerTimeout } from 'components/UserActions/ServerTimeoutBanner';
import { useRecordStraddleTap } from 'components/UserActions/TableActions/StraddleTapWatch';

/**
 * How long a sent change is shown before the broadcast confirms it. Broadcasts
 * arrive every second (every 2 s for a background grid table), so after this the
 * send is taken as lost and the server's value is shown again.
 */
export const CONFIRM_TIMEOUT_MS = 5000;

/** A choice for hand `gameNo`: take the straddle, or not. */
type Choice = { gameNo: number; take: boolean };

/**
 * The server's "straddle next hand" offer for this player, or null. Absent (not
 * just null) from a broadcaster that predates EN-11023.
 */
export const useStraddlePreActionOffer = () =>
  useTableSelector((table) => table.gameState?.pokerActions?.straddlePreAction ?? null);

/**
 * EN-11023: "Straddle next hand", the straddle as a pre-action.
 *
 * The server decides who sees it (`pokerActions.straddlePreAction`, only for the
 * next hand's UTG once they have no decision left in this hand: straight after
 * a fold, or once the betting is over, e.g. as an uncalled bet comes back) and
 * whether it posts (case "3" re-checks the seat and today's rules). This control
 * only shows the offer and sends the player's choice.
 *
 * Product (EN-11023 demo feedback): it sits in the action row, after Show/Muck
 * Cards, as a pre-selected action chip with the amount under the label, and it
 * toggles on and off up to the last moment before the cards are dealt, as at a
 * live table, staying up through the felt clearing and the next hand's set-up.
 * The server then moves the offer on (or withdraws it); a straddle left on
 * posts with the blinds, and a tap that arrives after the deal is refused, so
 * the unconfirmed change goes with the old offer; StraddleTapWatch then tells
 * the player if the dealt hand differs from their last change.
 *
 * In portrait it takes half the row, like Show/Muck Cards, never narrower than
 * its text nor wider than HAND_END_BUTTON_MAX_WIDTH (product, EN-11023
 * feedback): beside them the row splits 50/50, alone it sits centred, and on a
 * row too narrow for half it keeps its text width while they shrink.
 *
 * It always shows its checkbox, even while the pre-action checkbox flag leaves
 * the other chips colour-only: whether 2x BB will post must not rest on colour.
 *
 * Per table: it reads the table in TableIdContext and sends for that table, so
 * in the grid each table has its own.
 */
const StraddlePreActionButton = () => {
  const offer = useStraddlePreActionOffer();
  const { straddlePreAction } = useGameService();
  const { showServerTimeout } = useServerTimeout();
  const recordTap = useRecordStraddleTap();

  // `sent` is the change on its way to the server; `latest` is the player's last
  // tap, which the chip shows. One change is sent at a time: the take and the
  // cancel run as separate Lambda calls, so a second one sent before the first
  // is confirmed could land first and leave the older choice stored. A tap
  // while one is on its way is still taken at once (refusing it left the chip
  // dead for the 1-3 s a confirmation takes), and is sent once that one is
  // confirmed, if the server does not have it by then.
  const [sent, setSent] = useState<Choice | null>(null);
  const [latest, setLatest] = useState<Choice | null>(null);

  const latestApplies = !!offer && !!latest && latest.gameNo === offer.gameNo;
  const chosen = latestApplies ? latest.take : !!offer?.set;

  // Keyed on the offer's VALUES: the reducer hands over a new pokerActions
  // object whenever any of it changes (every bet at the table), and keying on
  // the object restarted the timer below each time, so it could never fire
  // mid-hand.
  const offerGameNo = offer?.gameNo;
  const offerSet = offer?.set;

  // The change sent is done once the broadcast agrees or the offer moves on to
  // another hand or goes away. If no confirmation arrives in time, it says
  // "taking longer than expected" and the server's value shows again, taps
  // made since included. Not keyed on `latest`, so tapping on cannot put the
  // timeout off.
  useEffect(() => {
    if (!sent) return undefined;
    if (offerGameNo !== sent.gameNo || offerSet === sent.take) {
      setSent(null);
      return undefined;
    }
    const id = setTimeout(() => {
      setSent(null);
      setLatest(null);
      showServerTimeout();
    }, CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [offerGameNo, offerSet, sent, showServerTimeout]);

  // With nothing on its way, the last tap is done once the server has it or the
  // offer moved on; otherwise it is sent now.
  useEffect(() => {
    if (sent || !latest) return;
    if (offerGameNo !== latest.gameNo || offerSet === latest.take) {
      setLatest(null);
      return;
    }
    if (straddlePreAction(latest.take, latest.gameNo, showServerTimeout)) {
      setSent(latest);
    } else {
      setLatest(null);
    }
  }, [offerGameNo, offerSet, sent, latest, straddlePreAction, showServerTimeout]);

  if (!offer) return null;

  // A tap with nothing on its way is sent at once, and one that could not be
  // sent (false) leaves the chip as it is.
  const onChange = (value: string) => {
    const choice = { gameNo: offer.gameNo, take: value !== '' };
    const waiting = !!sent && sent.gameNo === offer.gameNo;
    if (!waiting) {
      if (!straddlePreAction(choice.take, choice.gameNo, showServerTimeout)) return false;
      setSent(choice);
    }
    setLatest(choice);
    recordTap(choice);
    return true;
  };

  // Keyed by hand: CheckboxAction keeps its own copy of `checked`, synced in an
  // effect, so a new hand's offer starts from a fresh chip, never the last one's.
  return (
    <CheckboxAction
      key={offer.gameNo}
      neutralUntilSelected
      alwaysShowCheckbox
      maxWidth={HAND_END_BUTTON_MAX_WIDTH}
      activeVariant='raise'
      label='Straddle next hand'
      sublabel={currencyFormatter(offer.amount)}
      value='straddle'
      checked={chosen}
      onChange={onChange}
    />
  );
};

export default StraddlePreActionButton;
